import { ActionIcon, Box, Tooltip } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { tokens } from "@blurb/codex-react";
import { Flex, IconV3 } from "@rpi/openapi-core";
import React from "react";
import { CellLink } from "./CellLink";

interface CellIdProps {
  value: string;
  /** When provided, the id renders as a link to this path. */
  to?: string;
  /** Lead with an ellipsis and keep the suffix visible — for opaque ids (ULIDs) whose leading chars don't distinguish them. */
  truncateStart?: boolean;
}

/** Table cell for a long identifier, truncated to fit (width-adaptive), with the full value via tooltip and copy. */
export const CellId: React.FC<CellIdProps> = ({ value, to, truncateStart }) => {
  const clipboard = useClipboard({ timeout: 250 });

  // truncateStart flips truncation to the front with direction:rtl, so the CSS ellipsis lands on the
  // left and the distinguishing suffix stays visible as the column resizes. <bdi> isolates the value so
  // the rtl context doesn't reorder its (digit-heavy) characters.
  const inner = truncateStart ? <Box component="bdi">{value}</Box> : value;
  const idSx = {
    display: "block",
    minWidth: 0,
    fontFamily: tokens.font.family.mono,
    ...(truncateStart ? { direction: "rtl" as const, textAlign: "left" as const } : {}),
  };

  return (
    <Flex.Row gap={4} align="center" sx={{ flexWrap: "nowrap", minWidth: 0 }}>
      <Tooltip label={value} withArrow openDelay={300}>
        <Box sx={{ display: "block", minWidth: 0, maxWidth: "100%", font: "inherit", lineHeight: "inherit" }}>
          {to ? (
            <CellLink
              to={to}
              aria-label={value}
              className="text-ellipsis"
              sx={{ ...idSx, color: tokens.color.semantic.text.link, textDecoration: "underline" }}
            >
              {inner}
            </CellLink>
          ) : (
            <Box className="text-ellipsis" sx={idSx}>
              {inner}
            </Box>
          )}
        </Box>
      </Tooltip>
      <ActionIcon size="xs" variant="transparent" sx={{ flexShrink: 0 }} onClick={() => clipboard.copy(value)}>
        <IconV3 icon="content_copy" size={15} color="currentColor" />
      </ActionIcon>
    </Flex.Row>
  );
};
